import PropTypes from 'prop-types';
import React, { Component } from 'react';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import InvoiceStore from 'stores/accounting/InvoiceStore';
import SessionStore from 'stores/SessionStore';
import AccountingWebAPIUtils, {
  setInvoicePaymentMethod,
} from 'utils/AccountingWebAPIUtils';
import { getShipmentByOid, downloadPODs } from 'utils/ShipmentWebAPIUtils';
import { getDocumentsV2, getSignedUrl } from 'utils/DocumentsAPIUtils';
import ShipmentSearch from 'components/site/shipment-search';
import _ from 'underscore';
import Spinner from 'components/ui/Spinner';
import DataPoller from 'components/ui/DataPoller';
import InvoiceJobList from './v2/InvoiceJobList';
import InvoiceV2LineItems from './v2/InvoiceV2LineItems';
import InvoiceHeader from './v2/InvoiceHeader';
import { paymentMethods } from 'constants/accounting';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import Alert from 'components/ui/Alert';
import { showToast } from 'stores/ToastStore';
import { withTranslation } from 'react-i18next';
import {
  BadgeAlt as Badge,
  Button,
  Card,
  CardHeader,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@cargomatic/ui';
import { ChevronDown, DollarSign } from 'lucide-react';
import formatMoney from 'utils/formatters/Money';
import {
  isValidInvoiceForCreditMemo,
  transformPayload,
  transformRebillData,
} from 'utils/credit-memo';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { sluggify } from '../../../../utils/localization/utils';
import InvoiceStatusBadge from '../../../v2/InvoiceStatusBadge';
import CreditMemoModal from 'components/modules/shipments/accounting/CreditMemoModal';
import CreditMemoV2Modal from './v2/CreditMemoV2Modal';
import { generateCreditMemo, getOrder } from 'utils/OrderWebAPIUtils';
import { get, cloneDeep } from 'lodash';
import PaymentSummary from '../payment-summary-v2';
import PaymentHistory from '../payment-history';
import LogPaymentModal from '../log-payment-modal';
import PaymentMethodDialog from '../PaymentMethodDialog';
import HistoryTable from '../history-table';
import CreditMemoTable from './credit-memo';
import SendEmailModal from '../send-email-modal';
import { isLegManagementEnabled } from 'helpers/legs-helper';

const STRIPE = 'automated';
const ACH = 'manual';

const Loading = () => {
  return (
    <div
      data-testid="div-4b795707"
      className="absolute justify-center w-full"
      style={{ top: '50%' }}
    >
      <div data-testid="div-2f499e92" className="align-middle">
        <span data-testid="span-694b47d2" className="text-5xl text-green">
          <Spinner dataTestId="Spinner-0fa95b56" showMessage={false} />
        </span>
      </div>
    </div>
  );
};

function getStateFromStores(invoiceId) {
  const invoice = InvoiceStore.getInvoice(invoiceId);
  let balance = 0;
  if (invoice) balance = invoice.amount_due;
  return {
    invoice: invoice,
    balance: balance,
    loading: false,
    editMode: false,
    updates: [],
  };
}

const JOBLIST_ACTIONS = {
  ADD: 'add',
  REMOVE: 'remove',
};

class InvoiceDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      selectedValue: '',
      invoiceId: props.invoiceId || props.match?.params?.id,
      isAdmin: SessionStore.isAdmin(),
      paymentMethod: null,
      showLineItemConfirmModal: false,
      isSavingPaymentMethod: false,
      showPaymentMethodDialog: false,
      tempPaymentMethod: null,
      activeTab: 'jobs',
      showLogPaymentModal: false,
      confirmModal: this.confirmModalDefault(),
      isFinanceAdmin: SessionStore.isFinanceAdmin(),
      hasFinanceAdminRole: SessionStore.hasFinanceAdminRole(),
      invoice: {},
      canGenerateCreditMemo: SessionStore.canGenerateCreditMemo(),
      showCreditMemoModal: false,
      disableSaveCreditMemoButton: false,
      shipment: {},
      selectedLineItem: {},
      disableCreditMemoOption: false,
      invoiceErrors: null,
      regenerating: false,
      showSendEmailModal: false,
      downloadingPods: false,
    };
  }

  _onChangeInFlight = false;

  async componentDidMount() {
    InvoiceStore.register(this.onChange);
    AccountingActionCreators.getInvoice(
      this.state.invoiceId,
      SessionStore.getUserType(),
    );
    InvoiceStore.addChangeListener(this.handleInvoiceRegenerateUpdate);
  }

  componentWillUnmount() {
    InvoiceStore.unregister(this.onChange);
    InvoiceStore.removeChangeListener(this.handleInvoiceRegenerateUpdate);
  }

  componentDidUpdate(prevProps) {
    if (this.props.invoiceId !== prevProps.invoiceId) {
      this.setState({
        invoiceId: this.props.invoiceId,
      });
      AccountingActionCreators.getInvoice(
        this.props.invoiceId,
        SessionStore.getUserType(),
      );
    }
  }
  getShipmentData = async (oid) => {
    const { t } = this.props;
    this.setState({ loading: true });
    try {
      const result = await getShipmentByOid(oid);
      this.setState({ shipment: result });
    } catch {
      showToast(t('FAILED_TO_FETCH_SHIPMENT_DATA'), null, 'error');
      this.setState({ shipment: {} });
    }
    this.setState({ loading: false });
  };

  handleSaveCreditMemo = async (rebillData) => {
    const { t } = this.props;
    this.setState({ disableSaveCreditMemoButton: true });
    try {
      const { shipment, invoiceId, invoice } = this.state;
      const adjustedItems = (rebillData?.adjustedItems ?? []).map((item) => {
        return {
          ...item,
          notes: (item.notes ?? '').trim(),
        };
      });
      const data = {
        rebill: rebillData.rebill,
        adjustedItems,
      };
      const selectedCharges = transformPayload(data, shipment, invoice);
      const rebillInformation = transformRebillData(data, shipment);
      const sendEdi = true;
      const invoiceCreatedDate = invoice?.date_created || new Date();
      const orderId = shipment?.order?._id || shipment?.order;
      const result = await generateCreditMemo(
        orderId,
        invoiceId,
        invoiceCreatedDate,
        rebillInformation,
        selectedCharges,
        data.rebill,
        sendEdi,
      );
      this.setState({
        showCreditMemoModal: false,
        disableSaveCreditMemoButton: false,
        disableCreditMemoOption: result?.entity?.success,
      });
      showToast(t('CREDIT_MEMO_GENERATION_SUCCESS'));
    } catch {
      showToast(t('CREDIT_MEMO_GENERATION_FAILURE'), null, 'error');
      this.setState({
        showCreditMemoModal: false,
        disableSaveCreditMemoButton: false,
      });
    }
  };

  handleSaveCreditMemoV2 = async (saveData) => {
    const { t } = this.props;
    this.setState({ disableSaveCreditMemoButton: true });
    try {
      const {
        orderId,
        invoiceId,
        invoiceDate,
        selectedCharges,
        selectedOrderCharges,
        rebill,
        sendEdi,
        adjustedItems,
      } = saveData;
      const { rebillInformation, rebillOrderItems } =
        this.buildRebillInformation(adjustedItems, rebill);
      const result = await generateCreditMemo(
        orderId,
        invoiceId,
        invoiceDate,
        rebillInformation,
        selectedCharges,
        rebill,
        sendEdi,
        selectedOrderCharges,
        rebillOrderItems,
      );
      this.setState({
        showCreditMemoModal: false,
        disableSaveCreditMemoButton: false,
        disableCreditMemoOption: result?.entity?.success,
      });
      showToast(t('CREDIT_MEMO_GENERATION_SUCCESS'));
    } catch {
      showToast(t('CREDIT_MEMO_GENERATION_FAILURE'), null, 'error');
      this.setState({
        showCreditMemoModal: false,
        disableSaveCreditMemoButton: false,
      });
    }
  };

  buildRebillInformation = (adjustedItems, rebill) => {
    const rebillInformation = {};
    const rebillOrderItems = {};
    for (const item of adjustedItems) {
      const isOrderLevel = !item.shipment_id;
      const rebillEntry = {
        ...item,
        reason_code: item.reasonCode,
        notes: item.notes,
        shipper_unit_qty: item.units,
        shipper_rate: rebill ? item.amount : 0,
        type: item.type,
        billing_type: item.billing_type,
        value: item.total_amount,
      };
      if (item.type === 'shipper_cost') {
        delete rebillEntry.shipper_unit_qty;
        delete rebillEntry.shipper_rate;
      }
      if (isOrderLevel) {
        rebillOrderItems[item._id] = rebillEntry;
      } else {
        const key = item.shipment_id;
        if (!rebillInformation[key]) {
          rebillInformation[key] = { rebill_required: rebill };
        }
        rebillInformation[key][item._id] = rebillEntry;
      }
    }
    return { rebillInformation, rebillOrderItems };
  };

  getSelectedLineItem = (invoice = {}) => {
    let selectedLineItem = {};
    if (invoice && Object.keys(invoice).length) {
      const selectedInvoice = { _id: invoice._id, number: invoice.number };
      if (get(invoice, 'lineItemGroups[0].lineItems[0]', false)) {
        selectedLineItem = {
          ...invoice.lineItemGroups[0].lineItems[0],
          invoice: selectedInvoice,
        };
        return selectedLineItem;
      }
      selectedLineItem = { invoice: selectedInvoice };
    }

    return selectedLineItem;
  };

  onChange = async () => {
    if (this._onChangeInFlight) {
      this._onChangePending = true;
      return;
    }
    this._onChangeInFlight = true;
    const result = getStateFromStores(this.state.invoiceId);
    try {
      const invoiceState = cloneDeep(result);
      if (invoiceState?.invoice) {
        // Fetch the full order if it's not populated (just an ID string)
        const { order } = invoiceState.invoice;
        if (order && typeof order === 'string') {
          // Reuse the already-fetched order if the ID matches
          const cachedOrder = this.state.invoice?.order;
          if (
            cachedOrder &&
            typeof cachedOrder === 'object' &&
            (cachedOrder._id === order || cachedOrder.id === order)
          ) {
            invoiceState.invoice.order = cachedOrder;
          } else {
            try {
              const { entity } = await getOrder(order);
              invoiceState.invoice.order = entity?.order || order;
            } catch (e) {
              console.error('Error fetching order:', e);
            }
          }
        }

        if (
          invoiceState.invoice?.jobs?.length &&
          !Object.keys(this.state.shipment).length
        ) {
          await this.getShipmentData(invoiceState.invoice.jobs[0]);
        }

        this.setState({
          ...invoiceState,
          regenerating: false,
        });
      }
    } catch (e) {
      showToast(e?.message, null, 'error');
    } finally {
      this._onChangeInFlight = false;
      if (this._onChangePending) {
        this._onChangePending = false;
        this.onChange();
      }
    }
  };

  checkPricingManagerPermission = () => {
    const { t } = this.props;
    if (!this.state.isFinanceAdmin) {
      showToast(t('PERMISSION_REQUIRED'), t('PERMISSION_REQUIRED'), 'error');
      return false;
    }
    return true;
  };

  savePayment = (payment) => {
    AccountingActionCreators.recordPaymentForInvoice(
      this.state.invoiceId,
      payment,
    );
  };

  handleInvoiceRegenerateUpdate = () => {
    if (!InvoiceStore.getInvoiceRegenerationStatus()) {
      const invoice = InvoiceStore.getUpdatedInvoice();
      this.setState({
        regenerating: false,
        invoice: invoice ?? this.state.invoice,
      });
    }
  };

  regenerateInvoice = (_id, type, data) => {
    this.setState({ regenerating: true }, () =>
      AccountingWebAPIUtils.regenerateInvoice(_id, type, data),
    );
  };

  handlePaymentMethodSelect = async (value) => {
    if (!value) {
      return;
    }
    const method = value.toLowerCase();
    const currentMethod =
      this.state.paymentMethod || this.state.invoice?.payment_method;
    if (method && method !== currentMethod) {
      const previousMethod = currentMethod || null;
      const method_value = method === 'manual' ? 'manual' : 'automated';
      try {
        await setInvoicePaymentMethod(method_value, this.state.invoiceId);
        this.setState({ paymentMethod: method });
        showToast('Payment method updated', null, 'success');
      } catch (e) {
        console.error(e);
        this.setState({ paymentMethod: previousMethod });
        showToast('Failed to update payment method', null, 'error');
      }
    }
  };

  handleSavePaymentMethod = async () => {
    const { tempPaymentMethod } = this.state;
    if (tempPaymentMethod) {
      this.setState({ isSavingPaymentMethod: true });
      try {
        await this.handlePaymentMethodSelect(tempPaymentMethod);
        this.setState({ showPaymentMethodDialog: false });
      } finally {
        this.setState({ isSavingPaymentMethod: false });
      }
    } else {
      this.setState({ showPaymentMethodDialog: false });
    }
  };

  getPaymentMethodLabel() {
    const { t } = this.props;
    const selectedValue = this.defineDropdownSelectedValue();
    const method = paymentMethods.find((m) => m.value === selectedValue);
    return method ? t(method.localizationSlug) : t('SELECT_PAYMENT_METHOD');
  }

  handleJobRemove = async (invoice, lineItem) => {
    try {
      const updatedJobIds = (invoice.jobs ?? []).filter(
        (id) =>
          (typeof id === 'string' ? id : id?._id) !== lineItem.shipment_id,
      );
      this.setState({ showLineItemConfirmModal: false });
      const newInvoice = await AccountingWebAPIUtils.updateInvoiceJobs(
        invoice,
        updatedJobIds,
        JOBLIST_ACTIONS.REMOVE,
      );

      this.setState({
        invoice: newInvoice,
        confirmModal: this.confirmModalDefault(),
      });
    } catch (e) {
      showToast(e?.message, null, 'error');
    }
  };

  defineDropdownSelectedValue() {
    const { paymentMethod, invoice } = this.state;
    const payment_method = invoice?.payment_method;
    if (paymentMethod) {
      return paymentMethod === 'automated' ? STRIPE : ACH;
    }
    if (payment_method) {
      return payment_method === 'automated' ? STRIPE : ACH;
    }
    return '';
  }

  handleSearch = (invoice, value, job) => {
    if (!this.checkPricingManagerPermission()) return;
    const originalGroups =
      this.state.originalLineItemGroups ?? invoice.lineItemGroups ?? [];
    const searchedShipmentId = job.shipmentId;
    const listOfJobs = originalGroups.filter(
      (jobsAssociated) => jobsAssociated.shipmentId === searchedShipmentId,
    );
    if (listOfJobs.length) {
      this.setState({
        selectedValue: value,
        originalLineItemGroups: originalGroups,
        invoice: { ...invoice, lineItemGroups: listOfJobs },
      });
    } else {
      this.setState({ selectedValue: value });
    }
  };

  onSelectChange = (value) => {
    const invoiceState = getStateFromStores(this.state.invoiceId);
    const { selectedValue, originalLineItemGroups } = this.state;
    const lineItemGroups =
      originalLineItemGroups ?? invoiceState.invoice?.lineItemGroups ?? [];
    value = value.trim().toUpperCase();
    if (value.length && value !== selectedValue) {
      const filteredJobs = lineItemGroups.filter((lineItemGroup) =>
        lineItemGroup.shipmentId?.includes(value),
      );
      this.setState({
        invoice: { ...invoiceState.invoice, lineItemGroups: filteredJobs },
      });
    } else {
      this.setState({
        invoice: invoiceState.invoice ?? this.state.invoice,
        originalLineItemGroups: null,
        selectedValue: '',
      });
    }
  };

  setChanges = (updates) => {
    const { editMode } = this.state;
    this.setState({ updates });

    if (!editMode) {
      this.setState({ editMode: true });
    }
  };

  confirmModalDefault = () => {
    return {
      title: '',
      message: '',
      onConfirm: null,
    };
  };

  confirmRemoveShipment = (job) => {
    if (!this.checkPricingManagerPermission()) return;
    const { t } = this.props;
    const title = t('REMOVE_SHIPMENT_FROM_INVOICE_TITLE', {
      shipment_id: job.shipmentId,
    });
    const message = t('REMOVE_SHIPMENT_FROM_INVOICE_MESSAGE');
    this.setState((prevState) => ({
      showLineItemConfirmModal: true,
      confirmModal: {
        title,
        message,
        onConfirm: () => this.handleJobRemove(prevState.invoice, job),
      },
    }));
  };

  isDebitInvoiceType = () => {
    return (
      !this.state.invoice?.invoice_type ||
      this.state.invoice?.invoice_type === 'debit'
    );
  };

  openCreditMemoModal = () => {
    if (this.state.canGenerateCreditMemo) {
      this.setState({ showCreditMemoModal: true });
    }
  };

  sendEmail = (emails) => {
    const { invoice, invoiceId } = this.state;
    const invoice_type = invoice?.invoice_type || 'debit';
    const isCreditMemo = invoice_type === 'credit';
    AccountingActionCreators.emailInvoice(invoiceId, emails, isCreditMemo);
  };

  sendEdiInvoice = async () => {
    const { t } = this.props;
    try {
      const response = await AccountingWebAPIUtils.sendEdiInvoice(
        this.state.invoice._id,
      );
      if (response.entity && response?.entity?.success) {
        AccountingActionCreators.getInvoice(this.state.invoice._id);
      } else {
        showToast(t('EDI_SEND_FAILED'), null, 'error');
      }
    } catch (e) {
      showToast(e?.message, null, 'error');
    }
  };

  handlePODDownload = async () => {
    this.setState({ downloadingPods: true });
    try {
      await downloadPODs({ invoiceId: this.state.invoice._id });
    } catch (e) {
      showToast(e?.message, null, 'error');
    }
    this.setState({ downloadingPods: false });
  };

  handleDownload = async () => {
    const { t } = this.props;
    const newWindow = window.open('', '_blank');
    try {
      const result = await getDocumentsV2([this.state.invoice._id], {
        options: {
          order: {
            field: 'createdAt',
            direction: 'desc',
          },
          limit: 1,
        },
      });
      if ('errors' in result) {
        throw new Error(
          result.errors[0]?.message || 'Failed to fetch invoice document',
        );
      }

      const key = result?.data?.getDocumentsV2?.documents?.[0]?.key;
      const { data: signedUrl } = await getSignedUrl(key);
      const url = signedUrl?.getSignedUrl?.signedUrl;
      if (url) {
        if (newWindow) {
          newWindow.opener = null;
          newWindow.location.href = url;
        }
      } else {
        newWindow?.close();
        showToast(t('GET_SIGNED_URL_ERROR'), null, 'error');
      }
    } catch (e) {
      newWindow?.close();
      showToast(t('GET_SIGNED_URL_ERROR'), e.message, 'error');
    }
  };

  renderInvoiceV1() {
    const { t } = this.props;
    const { invoice, isAdmin, invoiceErrors, disableCreditMemoOption } =
      this.state;
    const { title, message, onConfirm } = this.state.confirmModal;
    const showInvoiceErrors = invoiceErrors && !_.isEmpty(invoiceErrors);
    const selectedValue = this.defineDropdownSelectedValue();
    const { _id, type, order } = invoice;
    const orderId = typeof order === 'string' ? order : (order?._id ?? '');
    const date_created = invoice?.date_created || new Date();
    const isMultiJobInvoice = invoice?.jobs?.length > 1;
    const disableCMBtn =
      (invoice?.credit_memos || []).length ||
      disableCreditMemoOption ||
      isMultiJobInvoice;

    return (
      <div
        data-testid="div-f104d4f9"
        className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8"
      >
        {showInvoiceErrors && (
          <Alert
            dataTestId="Alert-4eedc2d6"
            type="warning"
            title={invoiceErrors.title}
          >
            <div data-testid="div-57ba8b68">{invoiceErrors.body}</div>
          </Alert>
        )}

        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 data-testid="h2-0c2badfe" className="my-0">
              {invoice.number}
            </h2>
            {isAdmin && this.isDebitInvoiceType() && invoice.edi_sent && (
              <Badge
                variant="outline"
                data-testid="CargomaticBadge-c98247c2"
                className="border-emerald-200 bg-emerald-50 text-emerald-700"
              >
                {t('EDI_SENT')}
              </Badge>
            )}
            {invoice.missing_pods != null && (
              <Badge
                variant="outline"
                data-testid="CargomaticBadge-pod-status"
                className={
                  invoice.missing_pods
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                }
              >
                {invoice.missing_pods
                  ? t('WITHOUT_PODS_JOBS')
                  : t('ALL_PODS_RECEIVED')}
              </Badge>
            )}
            <span data-testid="CargomaticBadge-invoice-status">
              <InvoiceStatusBadge
                status={invoice.status}
                label={t(sluggify(invoice.status))}
              />
            </span>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && this.isDebitInvoiceType() && (
              <PaymentMethodDialog
                open={this.state.showPaymentMethodDialog}
                onOpenChange={(open) => {
                  this.setState({
                    showPaymentMethodDialog: open,
                    ...(open ? { tempPaymentMethod: selectedValue } : {}),
                  });
                }}
                selectedValue={selectedValue}
                tempValue={this.state.tempPaymentMethod}
                onTempValueChange={(value) =>
                  this.setState({ tempPaymentMethod: value })
                }
                onSave={this.handleSavePaymentMethod}
                isSaving={this.state.isSavingPaymentMethod}
                disabled={!this.state.hasFinanceAdminRole}
                label={this.getPaymentMethodLabel()}
                description={t('PAYMENT_METHOD_TOOLTIP_MESSAGE')}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  {t('ACTIONS')}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {this.state.canGenerateCreditMemo &&
                  isValidInvoiceForCreditMemo(invoice) && (
                    <DropdownMenuItem
                      className="gap-2"
                      disabled={!!disableCMBtn}
                      onClick={
                        disableCMBtn ? undefined : this.openCreditMemoModal
                      }
                    >
                      {t('CREATE_CREDIT_MEMO')}
                    </DropdownMenuItem>
                  )}
                <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() => this.setState({ showSendEmailModal: true })}
                  >
                    {t('EMAIL_INVOICE')}
                  </DropdownMenuItem>
                </AuthorizationWrapper>
                <DropdownMenuItem
                  className="gap-2"
                  onClick={this.handleDownload}
                >
                  {t('DOWNLOAD')}
                </DropdownMenuItem>
                {this.isDebitInvoiceType() && (
                  <DropdownMenuItem
                    className="gap-2"
                    disabled={this.state.downloadingPods}
                    onClick={() => this.handlePODDownload()}
                  >
                    {t('DOWNLOAD_PODS')}
                  </DropdownMenuItem>
                )}
                {isAdmin && this.isDebitInvoiceType() && (
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() =>
                      this.regenerateInvoice(_id, type, {
                        orderId,
                        invoiceDate: date_created,
                      })
                    }
                    disabled={this.state.regenerating}
                  >
                    {t('REGENERATE_INVOICE')}
                  </DropdownMenuItem>
                )}
                {isAdmin && this.isDebitInvoiceType() && !invoice.edi_sent && (
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={this.sendEdiInvoice}
                  >
                    {t('SEND_EDI')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Summary + Payments + Credit Memo grid */}
        <div className="flex w-full flex-col gap-4 lg:grid lg:grid-cols-4 lg:gap-4">
          <div className="col-span-1">
            <Card>
              <CardHeader>
                <h3 className="m-0 text-base font-semibold">{t('SUMMARY')}</h3>
              </CardHeader>
              <CardContent>
                {this.isDebitInvoiceType() ? (
                  <PaymentSummary
                    total={invoice.total}
                    paid={invoice.total_payments}
                    balance={invoice.amount_due}
                    credit_memos={invoice.credit_memos || []}
                    currency={invoice?.currency}
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-y-4 gap-x-2">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        {t('CREDIT')}
                      </div>
                      <div className="text-red-500 font-medium">
                        {formatMoney(invoice.total, invoice?.currency)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        {t('CREDITED_INVOICE')}
                      </div>
                      <a
                        data-testid="a-b0a4e65d"
                        href={`/invoices/${invoice.original_invoice?.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {invoice.original_invoice?.number}
                      </a>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          {this.isDebitInvoiceType() && (
            <div className="col-span-3 flex flex-col gap-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h3 className="my-0 text-base font-semibold">
                      {t('PAYMENTS')}
                    </h3>
                    {isAdmin && (
                      <Button
                        size="sm"
                        className="bg-zinc-900 text-white hover:bg-zinc-800 gap-2"
                        data-testid="button-17f95155"
                        onClick={() =>
                          this.setState({ showLogPaymentModal: true })
                        }
                      >
                        <DollarSign className="h-4 w-4" />
                        {t('RECORD_PAYMENT')}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <PaymentHistory
                    payments={
                      invoice.payments?.filter(
                        (payment) => payment.type === 'payment',
                      ) || []
                    }
                    currency={invoice?.currency}
                  />
                </CardContent>
              </Card>
              {/* Credit Memo */}
              {invoice.invoice_type !== 'credit' &&
                invoice.credit_memos?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <h3 className="my-0 text-base font-semibold">
                        {t('CREDIT_MEMO', { count: 5 })}
                      </h3>
                    </CardHeader>
                    <CardContent>
                      <CreditMemoTable creditMemoList={invoice.credit_memos} />
                    </CardContent>
                  </Card>
                )}
            </div>
          )}
        </div>

        {/* Tabbed section: Line Items + Audit History */}
        <Card>
          <Tabs
            defaultValue="jobs"
            className="w-full"
            onValueChange={(value) => this.setState({ activeTab: value })}
          >
            <CardHeader className="pb-3">
              <TabsList className="justify-start">
                <TabsTrigger value="jobs">{t('LINE_ITEMS')}</TabsTrigger>
                <TabsTrigger value="audit">{t('AUDIT_HISTORY')}</TabsTrigger>
              </TabsList>
            </CardHeader>

            <TabsContent value="jobs" className="mt-0">
              <div className="px-6 pb-4">
                <div className="w-full sm:w-80">
                  <ShipmentSearch
                    isFilter
                    lineItemGroups={invoice.lineItemGroups ?? []}
                    dataTestId="ShipmentSearch-ja42ew24"
                    onSelect={(value, job) => {
                      this.handleSearch(invoice, value, job);
                    }}
                    onChange={(value) => this.onSelectChange(value)}
                    disabled={!this.state.isFinanceAdmin}
                  />
                </div>
              </div>
              <CardContent className="overflow-x-auto">
                <InvoiceJobList invoice={invoice} />
              </CardContent>
            </TabsContent>

            <TabsContent value="audit" className="mt-0">
              <CardContent>
                <HistoryTable data={invoice.audit || []} type="Invoice" />
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Modals */}
        <DataPoller
          dataTestId="DataPoller-32de1597"
          frequency={60000}
          action={() => this.onChange()}
        />
        {this.state.showLogPaymentModal && (
          <LogPaymentModal
            show={this.state.showLogPaymentModal}
            amount={(this.state.balance || 0).toFixed(2)}
            onSave={this.savePayment}
            onHide={() => this.setState({ showLogPaymentModal: false })}
          />
        )}
        {this.state.showCreditMemoModal && (
          <CreditMemoModal
            selectedLineItem={this.getSelectedLineItem(this.state.invoice)}
            showModal={this.state.showCreditMemoModal}
            shipment={this.state.shipment}
            onClose={() => this.setState({ showCreditMemoModal: false })}
            disableSave={this.state.disableSaveCreditMemoButton}
            onSave={(data) => this.handleSaveCreditMemo(data)}
          />
        )}
        <SendEmailModal
          show={this.state.showSendEmailModal}
          onSend={this.sendEmail}
          onHide={() => this.setState({ showSendEmailModal: false })}
        />
        <ConfirmModal
          dataTestId="ConfirmModal-d648aa58"
          show={this.state.showLineItemConfirmModal}
          title={title}
          message={message}
          onConfirm={onConfirm}
          onHide={() => this.setState({ showLineItemConfirmModal: false })}
        />
      </div>
    );
  }

  renderInvoiceV2() {
    const { t } = this.props;
    const { invoice, isAdmin, invoiceErrors, disableCreditMemoOption } =
      this.state;
    const { title, message, onConfirm } = this.state.confirmModal;
    const showInvoiceErrors = invoiceErrors && !_.isEmpty(invoiceErrors);
    const selectedValue = this.defineDropdownSelectedValue();

    return (
      <div
        data-testid="div-f104d4f9"
        className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8"
      >
        {showInvoiceErrors && (
          <Alert
            dataTestId="Alert-4eedc2d6"
            type="warning"
            title={invoiceErrors.title}
          >
            <div data-testid="div-57ba8b68">{invoiceErrors.body}</div>
          </Alert>
        )}

        {/* Overview Card */}
        <InvoiceHeader
          invoice={invoice}
          invoiceId={this.state.invoiceId}
          isAdmin={isAdmin}
          isModal={!!this.props.invoiceId}
          canGenerateCreditMemo={this.state.canGenerateCreditMemo}
          disableCreditMemoOption={disableCreditMemoOption}
          onOpenCreditMemoModal={this.openCreditMemoModal}
          onRegenerateInvoice={this.regenerateInvoice}
          regenerating={this.state.regenerating}
        />

        {/* Tabbed Content Card */}
        <Card>
          <Tabs
            defaultValue="jobs"
            className="w-full"
            onValueChange={(value) => this.setState({ activeTab: value })}
          >
            <CardHeader className="pb-3">
              <TabsList className="justify-start">
                <TabsTrigger value="jobs">{t('LINE_ITEMS')}</TabsTrigger>
                {this.isDebitInvoiceType() && (
                  <TabsTrigger value="payments">{t('PAYMENTS')}</TabsTrigger>
                )}
                <TabsTrigger value="audit">{t('AUDIT_HISTORY')}</TabsTrigger>
                {invoice?.invoice_type !== 'credit' &&
                  invoice?.credit_memos?.length > 0 && (
                    <TabsTrigger value="credit-memos">
                      {t('CREDIT_MEMO')}
                    </TabsTrigger>
                  )}
              </TabsList>
            </CardHeader>

            {/* Line Items Tab */}
            <TabsContent value="jobs" className="mt-0">
              <CardContent className="overflow-x-auto">
                {invoice.type === 'drayage' ? (
                  <InvoiceV2LineItems
                    invoice={invoice}
                    orderId={
                      typeof invoice.order === 'string'
                        ? invoice.order
                        : invoice.order?._id
                    }
                  />
                ) : (
                  <InvoiceJobList invoice={invoice} />
                )}
              </CardContent>
            </TabsContent>

            {/* Payments Tab */}
            {this.isDebitInvoiceType() && (
              <TabsContent value="payments" className="mt-0">
                <CardContent className="space-y-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-base font-semibold my-0">
                      Payment History
                    </h3>
                    <div className="flex flex-wrap items-center gap-3">
                      {isAdmin && (
                        <PaymentMethodDialog
                          open={this.state.showPaymentMethodDialog}
                          onOpenChange={(open) => {
                            this.setState({
                              showPaymentMethodDialog: open,
                              ...(open
                                ? { tempPaymentMethod: selectedValue }
                                : {}),
                            });
                          }}
                          selectedValue={selectedValue}
                          tempValue={this.state.tempPaymentMethod}
                          onTempValueChange={(value) =>
                            this.setState({ tempPaymentMethod: value })
                          }
                          onSave={this.handleSavePaymentMethod}
                          isSaving={this.state.isSavingPaymentMethod}
                          disabled={!this.state.hasFinanceAdminRole}
                          label={this.getPaymentMethodLabel()}
                          description={t('PAYMENT_METHOD_TOOLTIP_MESSAGE')}
                        />
                      )}
                      {isAdmin && (
                        <Button
                          size="sm"
                          className="bg-zinc-900 text-white hover:bg-zinc-800 gap-2"
                          data-testid="button-17f95155"
                          onClick={() =>
                            this.setState({ showLogPaymentModal: true })
                          }
                        >
                          <DollarSign className="h-4 w-4" />
                          {t('RECORD_PAYMENT')}
                        </Button>
                      )}
                    </div>
                  </div>
                  <PaymentHistory
                    payments={
                      invoice.payments?.filter(
                        (payment) => payment.type === 'payment',
                      ) || []
                    }
                    currency={invoice?.currency}
                  />
                </CardContent>
              </TabsContent>
            )}

            {/* Audit History Tab */}
            <TabsContent value="audit" className="mt-0">
              <CardContent>
                <HistoryTable data={invoice.audit || []} type="Invoice" />
              </CardContent>
            </TabsContent>

            {/* Credit Memos Tab */}
            {invoice?.invoice_type !== 'credit' &&
              invoice?.credit_memos?.length > 0 && (
                <TabsContent value="credit-memos" className="mt-0">
                  <CardContent>
                    <CreditMemoTable creditMemoList={invoice?.credit_memos} />
                  </CardContent>
                </TabsContent>
              )}
          </Tabs>
        </Card>

        {/* Modals */}
        <DataPoller
          dataTestId="DataPoller-32de1597"
          frequency={60000}
          action={() => this.onChange()}
        />
        {this.state.showLogPaymentModal && (
          <LogPaymentModal
            show={this.state.showLogPaymentModal}
            amount={(this.state.balance || 0).toFixed(2)}
            onSave={this.savePayment}
            onHide={() => this.setState({ showLogPaymentModal: false })}
          />
        )}
        {this.state.showCreditMemoModal && (
          <CreditMemoV2Modal
            showModal={this.state.showCreditMemoModal}
            invoice={this.state.invoice}
            onClose={() => this.setState({ showCreditMemoModal: false })}
            disableSave={this.state.disableSaveCreditMemoButton}
            onSave={(data) => this.handleSaveCreditMemoV2(data)}
          />
        )}
        <ConfirmModal
          dataTestId="ConfirmModal-d648aa58"
          show={this.state.showLineItemConfirmModal}
          title={title}
          message={message}
          onConfirm={onConfirm}
          onHide={() => this.setState({ showLineItemConfirmModal: false })}
        />
      </div>
    );
  }

  renderInvoice() {
    const order = this.state.invoice?.order;
    if (isLegManagementEnabled(order)) {
      return this.renderInvoiceV2();
    }
    return this.renderInvoiceV1();
  }

  render() {
    if (this.state.loading) {
      return <Loading />;
    }
    return this.renderInvoice();
  }
}

InvoiceDetails.propTypes = {
  match: PropTypes.object.isRequired,
  invoiceId: PropTypes.string,
};

export default withTranslation()(InvoiceDetails);
